/**
 * iOS Safari Keyboard Fix
 * Handles viewport issues when virtual keyboard appears/disappears on iOS Safari
 */

let initialViewportHeight = null;
let isKeyboardOpen = false;

/**
 * Detects if the device is iOS Safari
 */
const isIOSSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /ipad|iphone|ipod/.test(ua) && !window.MSStream;
  const isSafari = /safari/.test(ua) && !/chrome|crios|fxios/.test(ua);
  return isIOS && (isSafari || navigator.standalone);
};

/**
 * Gets the current viewport height
 */
const getViewportHeight = () => {
  return window.visualViewport?.height || window.innerHeight;
};

/**
 * Handles viewport changes when keyboard appears/disappears
 */
const handleViewportChange = () => {
  if (!isIOSSafari()) return;

  const currentHeight = getViewportHeight();
  
  if (initialViewportHeight === null) {
    initialViewportHeight = currentHeight;
    return;
  }

  const heightDifference = initialViewportHeight - currentHeight;
  const threshold = 150; // Minimum height difference to consider keyboard as open
  
  const wasKeyboardOpen = isKeyboardOpen;
  isKeyboardOpen = heightDifference > threshold;

  if (wasKeyboardOpen !== isKeyboardOpen) {
    // Keyboard state changed
    if (isKeyboardOpen) {
      handleKeyboardOpen(heightDifference);
    } else {
      handleKeyboardClose();
    }
  }
};

/**
 * Handles when keyboard opens
 */
const handleKeyboardOpen = (keyboardHeight) => {
  document.body.classList.add('ios-keyboard-open');
  
  // Dispatch custom event for components to listen to
  window.dispatchEvent(new CustomEvent('ios-keyboard-open', {
    detail: { keyboardHeight }
  }));
};

/**
 * Handles when keyboard closes
 */
const handleKeyboardClose = () => {
  document.body.classList.remove('ios-keyboard-open');
  
  // Dispatch custom event for components to listen to
  window.dispatchEvent(new CustomEvent('ios-keyboard-close'));
};

/**
 * Prevents iOS zoom on input focus
 */
const preventIOSZoom = () => {
  if (!isIOSSafari()) return;

  // Add event listener to all input elements
  const handleFocus = (event) => {
    const element = event.target;
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      // Temporarily set font-size to 16px to prevent zoom
      const originalFontSize = element.style.fontSize;
      element.style.fontSize = '16px';
      
      // Restore original font-size after a short delay
      setTimeout(() => {
        element.style.fontSize = originalFontSize;
      }, 100);
    }
  };

  document.addEventListener('focusin', handleFocus, true);
};

/**
 * Initialize iOS keyboard fixes
 */
export const initIOSKeyboardFix = () => {
  if (!isIOSSafari()) return;

  // Set initial viewport height
  initialViewportHeight = getViewportHeight();

  // Listen for viewport changes
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportChange);
  } else {
    // Fallback for older iOS versions
    window.addEventListener('resize', handleViewportChange);
  }

  // Prevent iOS zoom on input focus
  preventIOSZoom();

  // Add CSS class to body for iOS-specific styling
  document.body.classList.add('ios-safari');
};

/**
 * Cleanup function
 */
export const cleanupIOSKeyboardFix = () => {
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', handleViewportChange);
  } else {
    window.removeEventListener('resize', handleViewportChange);
  }
  
  document.body.classList.remove('ios-safari', 'ios-keyboard-open');
};

/**
 * Check if keyboard is currently open
 */
export const isIOSKeyboardOpen = () => isKeyboardOpen;

export default {
  initIOSKeyboardFix,
  cleanupIOSKeyboardFix,
  isIOSKeyboardOpen,
  isIOSSafari
};
